import * as React from 'react';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  SxProps,
  Theme,
  ButtonProps
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { League, Player, Contract, Team, TradeProposalPlayer, TradeProposalPlayerActionEnum } from '../../../sdk/model';
import { teamApi } from '../../../adapters/APIExporter';
import TradeDetails from '../trade/TradeDetails';
import { isPlayerLocked } from '../../util/PlayerDataUtil';
import { getMaxTeamSize } from '../../util/TeamUtils';

interface DropPlayerButtonProps extends Omit<ButtonProps, 'onClick' | 'disabled' | 'sx'> {
  leagueId: string;
  player: Player;
  contract: Contract;
  currentSeasonYear: number;
  team: Team;
  callback: (result: any) => void;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  commish?: boolean;
}

const DropPlayerButton: React.FC<DropPlayerButtonProps> = ({
  leagueId,
  player,
  contract,
  currentSeasonYear,
  team,
  callback,
  sx = {},
  commish = false,
  ...buttonProps
}) => {
  const [confirming, setConfirming] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const dropPlayer = async () => {
    try {
      teamApi.removePlayerFromTeam(player.id, team.id, commish).then(response => {
        callback(response.data);
        setConfirming(false);
      });
    } catch (error) {
      setErrors([error.response.data.message]);
      callback({ success: false, error });
    }
  };

  const players: TradeProposalPlayer[] = [{
    playerId: player.id,
    fromTeamId: team.id,
    action: TradeProposalPlayerActionEnum.Drop
  }];

  const user = useAppSelector((state) => state.user);
  
  const league: League | undefined = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: true,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: false,
    loadRosterFor: [],
    loadScheduleData: false,
    loadWaivedContracts: false,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete
  });

  const playerMap = new Map([[player.id, player]]);
  const teamMap = new Map([[team.id, team]]);
  const teamPlayerMap = new Map(Object.values(team.playerRoster || {}).flatMap(playerList => 
    playerList.filter(player => player).map(player => [player.playerId, player])
  ));
  team.practiceSquad?.filter(player => player).forEach(player => teamPlayerMap.set(player.playerId, player));
  const maxRosterSize = getMaxTeamSize(league!);
  const buttonDisabled = isPlayerLocked(contract);

  return (
    <Box>
      <Button
        variant="contained"
        color="error"
        onClick={() => setConfirming(true)}
        disabled={buttonDisabled}
        fullWidth={buttonProps.fullWidth || false}
        sx={sx}
        {...buttonProps}
      >
        Drop
      </Button>
      <Dialog 
        open={confirming} 
        onClose={() => setConfirming(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Confirm Drop</Typography>
            <IconButton 
              onClick={() => setConfirming(false)} 
              size="large" 
              edge="end"
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TradeDetails
            leagueId={leagueId}
            teams={[team]}
            currentSeason={currentSeasonYear}
            players={players}
            picks={[]}
            playerMap={playerMap}
            teamMap={teamMap}
            teamPlayerMap={teamPlayerMap}
            pickMap={new Map()}
            salaryCap={league?.currentSeason?.nflSeason?.salaryCap || 0}
            maxRosterSize={Number(maxRosterSize)}
          />
          {errors.length > 0 && (
            <Box color="error.main" mt={2}>
              {errors.map((error, index) => (
                <Typography key={index} color="error">
                  {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirming(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={dropPlayer} 
            color="error" 
            variant="contained"
          >
            Confirm Drop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DropPlayerButton;